/**
 * Generative UI Agent
 * Uses Thesys C1 API to create dynamic, AI-generated user interfaces
 */

interface ThesysMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ThesysCompletionRequest {
  model?: string;
  messages: ThesysMessage[];
  stream?: boolean;
  temperature?: number;
  max_tokens?: number;
}

interface ThesysCompletionResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: {
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }[];
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

class GenerativeUIAgent {
  private apiKey: string;
  private baseUrl: string;

  constructor() {
    this.apiKey = process.env.THESYS_API_KEY || '';
    this.baseUrl = process.env.THESYS_API_BASE_URL || 'https://api.thesys.dev/v1/embed';
  }

  /**
   * Generate UI component from natural language prompt
   */
  async generateUI(prompt: string, context?: string): Promise<string> {
    const messages: ThesysMessage[] = [
      {
        role: 'system',
        content:
          'You are a UI generation assistant. Create interactive, themeable UI components based on user requests.',
      },
    ];

    if (context) {
      messages.push({
        role: 'system',
        content: `Context: ${context}`,
      });
    }

    messages.push({
      role: 'user',
      content: prompt,
    });

    try {
      const response = await this.makeRequest({
        messages,
        temperature: 0.7,
        max_tokens: 2000,
      });

      return response.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('Error generating UI:', error);
      throw error;
    }
  }

  /**
   * Generate a dashboard component
   */
  async generateDashboard(dataDescription: string, metrics: string[]): Promise<string> {
    const prompt = `Create an interactive dashboard that displays ${dataDescription}.
    Include the following metrics: ${metrics.join(', ')}.
    The dashboard should have charts with tooltips and be responsive.`;

    return this.generateUI(prompt, 'farm analytics dashboard');
  }

  /**
   * Generate a form component
   */
  async generateForm(formPurpose: string, fields: string[]): Promise<string> {
    const prompt = `Create an interactive form for ${formPurpose}.
    Include these fields: ${fields.join(', ')}.
    The form should validate input and provide clear error messages.`;

    return this.generateUI(prompt, 'data collection form');
  }

  /**
   * Generate a conversational interface
   */
  async generateChatInterface(
    agentName: string,
    capabilities: string[]
  ): Promise<string> {
    const prompt = `Create a conversational chat interface for ${agentName}.
    The agent can help with: ${capabilities.join(', ')}.
    Include message history, input field, and send button.`;

    return this.generateUI(prompt, 'conversational agent interface');
  }

  /**
   * Generate a data visualization
   */
  async generateVisualization(
    dataType: string,
    visualizationType: 'chart' | 'graph' | 'map' | 'table'
  ): Promise<string> {
    const prompt = `Create an interactive ${visualizationType} to visualize ${dataType}.
    Make it interactive with tooltips and clickable elements where appropriate.`;

    return this.generateUI(prompt, 'data visualization');
  }

  /**
   * Generate a product card/listing
   */
  async generateProductCard(productType: string, features: string[]): Promise<string> {
    const prompt = `Create an attractive product card for ${productType}.
    Highlight these features: ${features.join(', ')}.
    Include image placeholder, title, description, and call-to-action button.`;

    return this.generateUI(prompt, 'product listing');
  }

  /**
   * Generate search results interface
   */
  async generateSearchResults(
    searchContext: string,
    filterOptions: string[]
  ): Promise<string> {
    const prompt = `Create a search results page for ${searchContext}.
    Include these filter options: ${filterOptions.join(', ')}.
    Show results in a grid or list with sorting options.`;

    return this.generateUI(prompt, 'search interface');
  }

  /**
   * Generate comparison table
   */
  async generateComparisonTable(
    itemType: string,
    comparisonMetrics: string[]
  ): Promise<string> {
    const prompt = `Create a comparison table for ${itemType}.
    Compare these metrics: ${comparisonMetrics.join(', ')}.
    Make it easy to scan and highlight differences.`;

    return this.generateUI(prompt, 'comparison interface');
  }

  /**
   * Stream UI generation (for real-time updates)
   */
  async* streamUI(prompt: string, context?: string): AsyncGenerator<string, void, unknown> {
    const messages: ThesysMessage[] = [
      {
        role: 'system',
        content:
          'You are a UI generation assistant. Create interactive, themeable UI components based on user requests.',
      },
    ];

    if (context) {
      messages.push({
        role: 'system',
        content: `Context: ${context}`,
      });
    }

    messages.push({
      role: 'user',
      content: prompt,
    });

    try {
      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          messages,
          stream: true,
          temperature: 0.7,
          max_tokens: 2000,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content;
              if (content) {
                yield content;
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error('Error streaming UI:', error);
      throw error;
    }
  }

  /**
   * Make request to Thesys API
   */
  private async makeRequest(
    request: ThesysCompletionRequest
  ): Promise<ThesysCompletionResponse> {
    const response = await fetch(`${this.baseUrl}/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: request.model || 'gpt-4',
        messages: request.messages,
        stream: request.stream || false,
        temperature: request.temperature || 0.7,
        max_tokens: request.max_tokens || 1000,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Thesys API error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  /**
   * Generate custom UI with specific requirements
   */
  async generateCustomUI(requirements: {
    purpose: string;
    features: string[];
    style?: string;
    interactions?: string[];
  }): Promise<string> {
    let prompt = `Create a UI component for ${requirements.purpose}.`;

    if (requirements.features.length > 0) {
      prompt += `\nFeatures to include: ${requirements.features.join(', ')}.`;
    }

    if (requirements.style) {
      prompt += `\nStyle: ${requirements.style}.`;
    }

    if (requirements.interactions && requirements.interactions.length > 0) {
      prompt += `\nInteractions: ${requirements.interactions.join(', ')}.`;
    }

    return this.generateUI(prompt);
  }
}

export default new GenerativeUIAgent();
